// PM-SHRI Module Types

// ============================================
// Activity Tree Types
// ============================================

export type ActivityTreeNodeType = 'head' | 'subHead' | 'activity' | 'subActivity';

export interface ActivityTreeNode {
  id: number;
  title: string;
  parentID: number;
  path: string | null;
  type: ActivityTreeNodeType;
  recurringType: number | null;
  meta: string; // JSON string like "{\"2023\":[1],\"2024\":[1,2,3]}"
  status: number;
  createdAt: string;
  updatedAT: string;
  children: ActivityTreeNode[];
}

export interface ActivityTreeResponse {
  success: boolean;
  data: ActivityTreeNode[];
}

// Expense Activity Tree Types (with financial data)
export interface ExpenseActivityTreeNode {
  id: number;
  title: string;
  parentID: number;
  path?: string | null;
  type: ActivityTreeNodeType;
  recurringType: number | null;
  meta?: string;
  status: number;
  totalSanctioned: number;
  /**
   * Sanctioned amount of heads currently marked "Continue" (status = 1).
   * NOT funds released — real releases live in amount_released and have no
   * per-activity dimension, so no per-activity released figure exists.
   */
  activeSanctionedAmount: number;
  totalSpent: number;
  recurringAmount: number;
  nonRecurringAmount: number;
  totalRecurringAmount: number;
  totalNonRecurringAmount: number;
  sancRecurring?: number;
  sancNonRecurring?: number;
  createdAt?: string;
  updatedAT?: string;
  children: ExpenseActivityTreeNode[];
}

export interface ExpenseActivityTreeResponse {
  success: boolean;
  data: {
    totalReleasedAmount: number;
    tree: ExpenseActivityTreeNode[];
  };
}

// Expenditure Activity Tree Types (with sanTotal and spentTotal)
export interface ExpenditureActivityTreeNode {
  id: number;
  title: string;
  sanTotal: number;
  spentTotal: number;
  sanRec: number;
  sanNonRec: number;
  spentRec: number;
  spentNonRec: number;
  parentID: number;
  children: ExpenditureActivityTreeNode[];
}

export interface ExpenditureActivityTreeResponse {
  success: boolean;
  data: {
    tree: ExpenditureActivityTreeNode[];
  };
}

// ============================================
// Core Data Structure Types
// ============================================

// The 6 Fixed Heads
export type HeadCode = 'H1' | 'H2' | 'H3' | 'H4' | 'H5' | 'H6';

export interface Head {
  id: number;
  code: HeadCode;
  name: string;
  description: string;
  color: string; // For visualization
  totalSanctioned: number;
  totalReceived: number; // Amount actually received from sanctioned
  totalSpent: number;
  totalUnspent: number;
  utilizationPercentage: number; // Based on received amount
  targetUnits: number; // Target units/quantity to be covered
  completedUnits: number; // Actual units covered
  efficiencyPercentage: number; // completedUnits / targetUnits * spendRatio
  subheadCount: number;
}

export interface Subhead {
  id: number;
  headId: number;
  headCode: HeadCode;
  headName: string;
  code: string;
  name: string;
  description: string;
  totalSanctioned: number;
  totalReceived: number;
  totalSpent: number;
  totalUnspent: number;
  utilizationPercentage: number;
  targetUnits: number;
  completedUnits: number;
  efficiencyPercentage: number;
  activityCount: number;
}

export interface Activity {
  id: number;
  subheadId: number;
  subheadName: string;
  headId: number;
  headName: string;
  code: string;
  name: string;
  description: string;
  expenditureType: ExpenditureType;
  totalSanctioned: number;
  totalReceived: number;
  totalSpent: number;
  totalUnspent: number;
  utilizationPercentage: number;
  targetUnits: number;
  completedUnits: number;
  efficiencyPercentage: number;
  subActivityCount: number;
}

export type ExpenditureType = 'recurring' | 'non-recurring';

export interface SubActivity {
  id: number;
  activityId: number;
  activityName: string;
  subheadId: number;
  subheadName: string;
  headId: number;
  headName: string;
  code: string;
  name: string;
  description: string;
  expenditureType: ExpenditureType;
  sanctionedAmount: number;
  spentAmount: number;
  unspentAmount: number;
  utilizationPercentage: number;
  status: SanctionStatus;
  schoolId: number;
  schoolName: string;
}

export type SanctionStatus =
  | 'fully_utilized'
  | 'partially_utilized'
  | 'not_utilized'
  | 'pending';

// ============================================
// School Performance Types
// ============================================

export interface SchoolPerformance {
  kvId: number;
  kvCode: string;
  schoolName: string;
  kvName: string; // Alias for schoolName
  roId: number;
  roName: string;
  region: string; // Alias for roName
  stateId: number;
  stateName: string;
  districtName: string;
  pmShriStatus: 'active' | 'inactive';
  pmShriPhase: PMShriPhase; // Phase 1, 2, 3, 5, 7

  // Financial Summary
  totalSanctioned: number;
  totalReceived: number; // Amount actually received
  totalSpent: number;
  totalUnspent: number;
  overallUtilization: number; // Based on received (spent/received)
  utilizationPercentage: number; // Alias for overallUtilization

  // Efficiency Metrics (Quantity-based)
  targetUnits: number;
  completedUnits: number;
  efficiencyPercentage: number; // Measures if funds are spent efficiently on intended units

  // Expenditure Type Breakdown
  recurringAmount: number;
  recurringSpent: number;
  recurringUtilization: number;
  nonRecurringAmount: number;
  nonRecurringSpent: number;
  nonRecurringUtilization: number;

  // Head-wise breakdown
  headWiseData: HeadWiseSchoolData[];

  // Performance metrics
  performanceScore: number; // 0-100
  rank: number;
  previousRank?: number;
  trend: 'up' | 'down' | 'stable';
}

export interface HeadWiseSchoolData {
  headId: number;
  headCode: HeadCode;
  headName: string;
  sanctioned: number;
  received: number;
  spent: number;
  unspent: number;
  utilization: number;
  targetUnits: number;
  completedUnits: number;
  efficiency: number;
}

// ============================================
// Region Performance Types
// ============================================

export interface RegionPerformance {
  roId: number;
  roName: string;
  roCode: string;
  totalSchools: number;
  activeSchools: number;
  pmShriSchools: number;

  totalSanctioned: number;
  totalReceived: number;
  totalSpent: number;
  totalUnspent: number;
  overallUtilization: number;

  // Efficiency Metrics
  targetUnits: number;
  completedUnits: number;
  efficiencyPercentage: number;

  // Expenditure Type Breakdown
  recurringAmount: number;
  recurringSpent: number;
  recurringUtilization: number;
  nonRecurringAmount: number;
  nonRecurringSpent: number;
  nonRecurringUtilization: number;

  headWiseData: HeadWiseRegionData[];

  topPerformers: SchoolPerformance[];
  bottomPerformers: SchoolPerformance[];
  topPerformingSchool?: string;
  lowestPerformingSchool?: string;

  performanceScore: number;
  rank: number;
  previousRank?: number;
  trend: 'up' | 'down' | 'stable';
}

export interface HeadWiseRegionData {
  headId: number;
  headCode: HeadCode;
  headName: string;
  color: string;
  totalSchools: number;
  sanctioned: number;
  received: number;
  spent: number;
  unspent: number;
  utilization: number;
  targetUnits: number;
  completedUnits: number;
  efficiency: number;
}

// ============================================
// Dashboard Metrics Types
// ============================================

export interface PMShriDashboardMetrics {
  // Overall Summary
  totalPMShriSchools: number;
  activeSchools: number;
  totalSanctioned: number;
  totalReceived: number;
  totalSpent: number;
  totalUnspent: number;
  overallUtilization: number; // Based on received (spent/received)

  // Efficiency Metrics
  targetUnits: number;
  completedUnits: number;
  overallEfficiency: number; // Combines utilization with unit coverage

  // Expenditure Type Summary
  recurringTotal: number;
  recurringSpent: number;
  recurringUtilization: number;
  nonRecurringTotal: number;
  nonRecurringSpent: number;
  nonRecurringUtilization: number;

  // Head-wise Summary
  headWiseSummary: Head[];

  // Performance Distribution
  fullyUtilizedSchools: number;
  partiallyUtilizedSchools: number;
  notUtilizedSchools: number;

  // Top/Bottom Performers
  topPerformingRegions: RegionPerformance[];
  bottomPerformingRegions: RegionPerformance[];
  topPerformingSchools: SchoolPerformance[];
  bottomPerformingSchools: SchoolPerformance[];

  // Trends
  monthlyTrends: MonthlyTrendData[];

  // Activity Summary
  totalActivities: number;
  completedActivities: number;
  pendingActivities: number;

  // Phase-wise Summary
  phaseWiseSummary: PhaseWiseSummary[];
}

export interface PhaseWiseSummary {
  phase: PMShriPhase;
  phaseName: string;
  totalSchools: number;
  schoolCount: number; // Alias for totalSchools for convenience
  activeSchools: number;
  totalSanctioned: number;
  totalReceived: number;
  totalSpent: number;
  utilization: number; // spent/received percentage
  utilizationPercentage: number; // Alias for utilization
  efficiencyPercentage: number; // Combines utilization with unit coverage
}

export interface MonthlyTrendData {
  month: string;
  year: number;
  sanctioned: number;
  received: number;
  spent: number;
  utilization: number;
  recurringSpent: number;
  nonRecurringSpent: number;
}

export interface MonthlyFundFlowTrendResponse {
  success: boolean;
  data: Record<string, {
    totalSanctioned: number;
    totalReceived: number;
    totalSpent: number;
    totalUtilization: string;
  }>;
}

// ============================================
// Analytics Types
// ============================================

export interface HeadAnalytics {
  head: Head;
  subheads: Subhead[];
  activities: Activity[];
  regionWiseData: HeadWiseRegionData[];
  schoolWiseData: {
    school: SchoolPerformance;
    headData: HeadWiseSchoolData;
  }[];
  trends: MonthlyTrendData[];
}

export interface ExpenditureAnalytics {
  type: ExpenditureType;
  totalSanctioned: number;
  totalSpent: number;
  utilization: number;

  byHead: {
    headCode: HeadCode;
    headName: string;
    sanctioned: number;
    spent: number;
    utilization: number;
  }[];

  byRegion: {
    roId: number;
    roName: string;
    sanctioned: number;
    spent: number;
    utilization: number;
  }[];

  topActivities: Activity[];
  trends: MonthlyTrendData[];
}

// ============================================
// Chart Data Types
// ============================================

export interface DonutChartData {
  name: string;
  value: number;
  color: string;
  percentage?: number;
}

export interface BarChartData {
  name: string;
  sanctioned: number;
  spent: number;
  unspent: number;
  utilization: number;
}

export interface TrendChartData {
  date: string;
  sanctioned: number;
  spent: number;
  utilization: number;
}

export interface HeatmapData {
  id: number;
  name: string;
  value: number; // Utilization percentage
  sanctioned: number;
  spent: number;
}

// ============================================
// Filter Types
// ============================================

export type PMShriPhase = 1 | 2 | 3 | 5 | 7;

export interface PMShriFilters {
  roId?: number;
  stateId?: number;
  kvId?: number;
  headCode?: HeadCode;
  subheadId?: number;
  expenditureType?: ExpenditureType;
  phase?: PMShriPhase;
  fromDate?: string;
  toDate?: string;
  financialYear?: string;
  utilizationRange?: {
    min: number;
    max: number;
  };
}

// ============================================
// API Response Types
// ============================================

export interface PMShriApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
  page?: number;
  pageSize?: number;
}

export interface SchoolListResponse {
  schools: SchoolPerformance[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RegionListResponse {
  regions: RegionPerformance[];
  total: number;
}

// ============================================
// Constants
// ============================================

export const HEAD_COLORS: Record<HeadCode, string> = {
  H1: '#6B1786', // Purple
  H2: '#10b981', // Green
  H3: '#f59e0b', // Orange
  H4: '#3b82f6', // Blue
  H5: '#ef4444', // Red
  H6: '#8b5cf6', // Violet
};

export const EXPENDITURE_COLORS: Record<ExpenditureType, string> = {
  'recurring': '#3b82f6',
  'non-recurring': '#10b981',
};

export const STATUS_COLORS: Record<SanctionStatus, string> = {
  'fully_utilized': '#10b981',
  'partially_utilized': '#f59e0b',
  'not_utilized': '#ef4444',
  'pending': '#6b7280',
};

export const DEFAULT_HEADS: Omit<Head, 'totalSanctioned' | 'totalReceived' | 'totalSpent' | 'totalUnspent' | 'utilizationPercentage' | 'targetUnits' | 'completedUnits' | 'efficiencyPercentage' | 'subheadCount'>[] = [
  { id: 1, code: 'H1', name: 'Curriculum, Pedagogy and Assessment', description: 'Teaching learning materials, assessment, vocational education, library, sports, and innovation projects', color: HEAD_COLORS.H1 },
  { id: 2, code: 'H2', name: 'Access Infrastructure - Adequacy, Functionality, Aesthetics and Safety', description: 'School strengthening, green initiatives, digital library, and ICT infrastructure', color: HEAD_COLORS.H2 },
  { id: 3, code: 'H3', name: 'Inclusive Practices and Gender Equity', description: 'Provisions for CWSN, self-defense training, and gender equity programs', color: HEAD_COLORS.H3 },
  { id: 4, code: 'H4', name: 'Management Monitoring and Governance', description: 'Program management, monitoring, measurement, evaluation, and research', color: HEAD_COLORS.H4 },
  { id: 5, code: 'H5', name: 'Beneficiary Satisfaction', description: 'Community participation through SMC/SMDC', color: HEAD_COLORS.H5 },
  { id: 6, code: 'H6', name: 'Human Resource and School Leadership', description: 'Capacity building through DIETs, NLIs, and teacher training programs', color: HEAD_COLORS.H6 },
];

// ============================================
// Activity Hierarchy Mapping
// ============================================

export interface ActivityNode {
  id: number;
  title: string;
  parentID: number;
  path: string;
  type: 'head' | 'subHead' | 'activity' | 'subActivity';
  recurringType: '0' | '1' | null;
  meta: Record<string, number[]>;
  status: number;
}

// Head 1: Curriculum, Pedagogy and Assessment - Subheads
export const H1_SUBHEADS = [
  { id: 7, title: 'Support at Pre-School Education / Bal-Vatika (ECCE)', path: '/1/7' },
  { id: 8, title: 'Teaching Learning Material for Primary Schools', path: '/1/8' },
  { id: 9, title: 'Learning Enhancement Programme (LEP) / Remedial Teaching', path: '/1/9' },
  { id: 10, title: 'Competency Based Assessment', path: '/1/10' },
  { id: 11, title: 'Holistic Report Cards', path: '/1/11' },
  { id: 12, title: 'Teacher Resource Material / Hand Book', path: '/1/12' },
  { id: 13, title: 'School Readiness Modules', path: '/1/13' },
  { id: 14, title: 'Rashtriya Aavishkar Abhiyaan', path: '/1/14' },
  { id: 15, title: 'Introduction of Vocational Education at Secondary and Sr. Secondary Level', path: '/1/15' },
  { id: 16, title: 'Library Grant', path: '/1/16' },
  { id: 17, title: 'Sports & Physical Education', path: '/1/17' },
  { id: 18, title: 'Project Innovation', path: '/1/18' },
];

// Head 2: Access Infrastructure - Subheads
export const H2_SUBHEADS = [
  { id: 19, title: 'Strengthening of Existing Schools (Excluding Pre-Primary)', path: '/2/19' },
  { id: 20, title: 'Green School', path: '/2/20' },
  { id: 21, title: 'Digital Library', path: '/2/21' },
  { id: 22, title: 'ICT and Digital Initiatives', path: '/2/22' },
  { id: 190, title: 'Annual School Grant', path: '/2/190' },
];

// Head 3: Inclusive Practices - Subheads
export const H3_SUBHEADS = [
  { id: 23, title: 'Provision for Children with Special Needs (CWSN)', path: '/3/23' },
  { id: 24, title: 'Gender and Equity', path: '/3/24' },
];

// Head 4: Management Monitoring - Subheads
export const H4_SUBHEADS = [
  { id: 25, title: 'Program Management', path: '/4/25' },
  { id: 174, title: 'Child Tracking', path: '/4/174' },
];

// Head 5: Beneficiary Satisfaction - Subheads
export const H5_SUBHEADS = [
  { id: 27, title: 'Community Participation SMC/SMDC', path: '/5/27' },
];

// Head 6: Human Resource - Subheads
export const H6_SUBHEADS = [
  { id: 28, title: 'Capacity Building Through DIETs / NLIs / KVs', path: '/6/28' },
];

// Activities mapping for each subhead
export const ACTIVITIES_BY_SUBHEAD: Record<number, ActivityNode[]> = {
  // H1 Subheads Activities
  7: [{ id: 29, title: 'Teaching Learning Materials', parentID: 7, path: '/1/7/29', type: 'activity', recurringType: null, meta: { 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 }],
  8: [{ id: 30, title: 'TLM / Supplimentary Materials for Class I-V', parentID: 8, path: '/1/8/30', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  9: [{ id: 31, title: 'Remedial Teaching', parentID: 9, path: '/1/9/31', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  10: [{ id: 32, title: 'Assessment in School', parentID: 10, path: '/1/10/32', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  11: [{ id: 33, title: 'Holistic Report Cards for Students', parentID: 11, path: '/1/11/33', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  12: [{ id: 34, title: 'Teacher Resource Material(TRM)', parentID: 12, path: '/1/12/34', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  13: [{ id: 35, title: 'School Readiness Modules (Class - I)', parentID: 13, path: '/1/13/35', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  14: [{ id: 36, title: 'Rashtriya Aavishkar Abhiyan (Class VI - XII)', parentID: 14, path: '/1/14/36', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  15: [
    { id: 37, title: 'Introduction to Vocational Education (NR)', parentID: 15, path: '/1/15/37', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 184, title: 'Introduction to Vocational Education (Rec)', parentID: 15, path: '/1/15/184', type: 'activity', recurringType: null, meta: { 2023: [1] }, status: 1 },
  ],
  16: [{ id: 38, title: 'Library Grant', parentID: 16, path: '/1/16/38', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  17: [{ id: 39, title: 'Sports and Physical Education ( Recurring)', parentID: 17, path: '/1/17/39', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  18: [
    { id: 40, title: 'Project Innovation (Non-Recurring)', parentID: 18, path: '/1/18/40', type: 'activity', recurringType: null, meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 41, title: 'Project Innovation (Recurring)', parentID: 18, path: '/1/18/41', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 180, title: 'Project Innovation (State Specific)', parentID: 18, path: '/1/18/180', type: 'activity', recurringType: null, meta: { 2023: [1] }, status: 1 },
  ],
  // H2 Subheads Activities
  19: [
    { id: 42, title: 'Strengthening of Existing Schools - NR', parentID: 19, path: '/2/19/42', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
    { id: 170, title: 'Strengthening of  Existing Schools-R', parentID: 19, path: '/2/19/170', type: 'activity', recurringType: null, meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  20: [{ id: 43, title: 'Green School', parentID: 20, path: '/2/20/43', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  21: [
    { id: 44, title: 'Tablet for School (Non-Recurring)', parentID: 21, path: '/2/21/44', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [2, 3], 2025: [2, 3, 5, 7] }, status: 1 },
    { id: 45, title: 'Tablet For School (Recurring)', parentID: 21, path: '/2/21/45', type: 'activity', recurringType: null, meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 7] }, status: 1 },
  ],
  22: [{ id: 46, title: 'ICT and Digital Initiatives - (NR) (Class VI - XII)', parentID: 22, path: '/2/22/46', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  // H3 Subheads Activities
  23: [{ id: 47, title: 'Student Oriented Component', parentID: 23, path: '/3/23/47', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 }],
  24: [
    { id: 48, title: 'Self-Defence Training', parentID: 24, path: '/3/24/48', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 49, title: 'Special Equity Projects - (NR)', parentID: 24, path: '/3/24/49', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 50, title: 'Special Equity Projects (Recurring)', parentID: 24, path: '/3/24/50', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // H4 Subheads Activities
  25: [{ id: 51, title: 'Program Management (MMMER)', parentID: 25, path: '/4/25/51', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  174: [{ id: 175, title: 'Child Tracking', parentID: 174, path: '/4/174/175', type: 'activity', recurringType: null, meta: { 2023: [1] }, status: 1 }],
  // H5 Subheads Activities
  27: [{ id: 53, title: 'Community Mobilization', parentID: 27, path: '/5/27/53', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  // H6 Subheads Activities
  28: [{ id: 54, title: 'Teacher Training and Capacity Building', parentID: 28, path: '/6/28/54', type: 'activity', recurringType: null, meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 }],
  // H2 Additional Activities
  190: [{ id: 191, title: 'Annual School Grant', parentID: 190, path: '/2/190/191', type: 'activity', recurringType: null, meta: { 2023: [1] }, status: 1 }],
};

// ============================================
// Sub-Activities Mapping by Activity
// ============================================

export const SUB_ACTIVITIES_BY_ACTIVITY: Record<number, Omit<ActivityNode, 'path'>[]> = {
  // Activity 29: Teaching Learning Materials
  29: [
    { id: 55, title: 'Science TLM', parentID: 29, type: 'subActivity', recurringType: '0', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
    { id: 56, title: 'Mathematics TLM', parentID: 29, type: 'subActivity', recurringType: '0', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
    { id: 57, title: 'Language TLM', parentID: 29, type: 'subActivity', recurringType: '0', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
    { id: 58, title: 'Social Studies TLM', parentID: 29, type: 'subActivity', recurringType: '0', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
  ],
  // Activity 30: TLM / Supplementary Materials
  30: [
    { id: 59, title: 'Worksheets and Practice Materials', parentID: 30, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 60, title: 'Question Banks', parentID: 30, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 31: Remedial Teaching
  31: [
    { id: 61, title: 'Bridging Classes', parentID: 31, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 62, title: 'Skill Development Sessions', parentID: 31, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 32: Assessment in School
  32: [
    { id: 63, title: 'Formative Assessment Tools', parentID: 32, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 64, title: 'Summative Assessment Framework', parentID: 32, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 33: Holistic Report Cards
  33: [
    { id: 65, title: 'Report Card Design & Printing', parentID: 33, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 66, title: 'Digital Reporting System', parentID: 33, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 34: Teacher Resource Material
  34: [
    { id: 67, title: 'Curriculum Guides', parentID: 34, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 68, title: 'Pedagogical Handbooks', parentID: 34, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 69, title: 'Subject Specific Manuals', parentID: 34, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 35: School Readiness Modules
  35: [
    { id: 70, title: 'Pre-Class Preparation Program', parentID: 35, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 71, title: 'Transition Support Materials', parentID: 35, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 36: Rashtriya Aavishkar Abhiyan
  36: [
    { id: 72, title: 'Innovation Project Kits', parentID: 36, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 73, title: 'Mentorship Programs', parentID: 36, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 37-39: Vocational Education & Sports
  37: [
    { id: 74, title: 'Vocational Course Content', parentID: 37, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 75, title: 'Industry Linkage Setup', parentID: 37, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  38: [
    { id: 76, title: 'Books Procurement', parentID: 38, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 77, title: 'Library Equipment & Furniture', parentID: 38, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 78, title: 'Digital Library Setup', parentID: 38, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  39: [
    { id: 79, title: 'Sports Equipment', parentID: 39, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 80, title: 'Sports Events & Competitions', parentID: 39, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 81, title: 'Coach Training Programs', parentID: 39, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activities 40-42: Project Innovation & School Strengthening
  40: [
    { id: 82, title: 'Innovation Lab Setup', parentID: 40, type: 'subActivity', recurringType: '0', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 83, title: 'Prototype Development', parentID: 40, type: 'subActivity', recurringType: '0', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  41: [
    { id: 84, title: 'Recurring Innovation Support', parentID: 41, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  42: [
    { id: 85, title: 'Classroom Infrastructure', parentID: 42, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
    { id: 86, title: 'Water & Sanitation Facilities', parentID: 42, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
    { id: 87, title: 'Boundary Wall & Compound', parentID: 42, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
  ],
  // Activity 43: Green School
  43: [
    { id: 88, title: 'Tree Plantation Drive', parentID: 43, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 89, title: 'Solar Panel Installation', parentID: 43, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 90, title: 'Waste Management System', parentID: 43, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activities 44-46: Digital Library & ICT
  44: [
    { id: 91, title: 'Tablet Hardware Procurement', parentID: 44, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [2, 3], 2025: [2, 3, 5, 7] }, status: 1 },
    { id: 92, title: 'Content Curation & Loading', parentID: 44, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [2, 3], 2025: [2, 3, 5, 7] }, status: 1 },
  ],
  45: [
    { id: 93, title: 'Tablet Management & Updates', parentID: 45, type: 'subActivity', recurringType: '1', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 7] }, status: 1 },
    { id: 94, title: 'Digital Content Subscription', parentID: 45, type: 'subActivity', recurringType: '1', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 7] }, status: 1 },
  ],
  46: [
    { id: 95, title: 'Computer Lab Setup', parentID: 46, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 96, title: 'Internet Connectivity', parentID: 46, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 97, title: 'ICT Training for Teachers', parentID: 46, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activities 47-51: Inclusive Practices & Management
  47: [
    { id: 98, title: 'CWSN Assessment', parentID: 47, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
    { id: 99, title: 'Assistive Devices Procurement', parentID: 47, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3] }, status: 1 },
  ],
  48: [
    { id: 100, title: 'Self-Defence Coaching', parentID: 48, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 101, title: 'Self-Defence Equipment', parentID: 48, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  49: [
    { id: 102, title: 'Gender Awareness Programs', parentID: 49, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 103, title: 'Menstrual Hygiene Management', parentID: 49, type: 'subActivity', recurringType: '0', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  50: [
    { id: 104, title: 'Girl Child Support Programs', parentID: 50, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  51: [
    { id: 105, title: 'Monitoring System Development', parentID: 51, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 106, title: 'Data Management & Analysis', parentID: 51, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 53: Community Mobilization
  53: [
    { id: 107, title: 'SMC Capacity Building', parentID: 53, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 108, title: 'Community Engagement Events', parentID: 53, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 54: Teacher Training
  54: [
    { id: 109, title: 'In-Service Teacher Training', parentID: 54, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 110, title: 'Training of Trainers Program', parentID: 54, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 111, title: 'Subject-Specific Workshops', parentID: 54, type: 'subActivity', recurringType: '1', meta: { 2023: [1], 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
  // Activity 170: Recurring School Strengthening
  170: [
    { id: 112, title: 'Maintenance & Repairs', parentID: 170, type: 'subActivity', recurringType: '1', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
    { id: 113, title: 'Consumables & Supplies', parentID: 170, type: 'subActivity', recurringType: '1', meta: { 2024: [1, 2, 3], 2025: [1, 2, 3, 5, 7] }, status: 1 },
  ],
};
